import React, { useState, useRef } from 'react';
import { FieldRow, InputField } from './shared/FormComponents';
import KeywordsInput from './shared/KeywordsInput';

const RoomSettingsModal = ({ isOpen, onClose, onSave, onDelete, initialData = null, contentOptions = [], onMapContent, resourceNo, mappedContent = null, onEditMappedContent, onCreateAndMapContent }) => {
  const [formData, setFormData] = useState(initialData || {
    객실명: '',
    영문명: '',
    기준인원: 2,
    최대인원: 2,
    추가비용부과: false,
    성인추가금: 0,
    아동추가금: 0,
    무료아동수: 0,
    가격차등적용: false,
    성인기준가: 0,
    아동기준가: 0,
    아동무료조건: false,
    아동무료연령: 5,
    조식포함: false,
    태그: '',
    어메니티: '',
  });

  const [selectedContentNo, setSelectedContentNo] = useState('');
  const currencySymbol = (initialData && initialData.통화기호) ? String(initialData.통화기호) : '원';

  const formatNumber = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return v === 0 ? '0' : '';
    return n.toLocaleString('ko-KR');
  };
  const parseNumberInput = (val) => {
    if (val == null) return 0;
    const s = String(val).replace(/[^0-9]/g, '');
    if (!s) return 0;
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const [mountedVisible, setMountedVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const backdropMouseDownRef = useRef(false);

  React.useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => setMountedVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setMountedVisible(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const src = initialData || {};
    const normalizeString = (v) => (v == null ? '' : String(v));
    const normalizeAmenities = (v) => {
      if (Array.isArray(v)) return v.filter(Boolean).map(String).join(', ');
      return v == null ? '' : String(v);
    };
    setFormData({
      객실명: src.객실명 ?? '',
      영문명: normalizeString(src.영문명 ?? src.객실영문명 ?? src.engName ?? src.영문 ?? src.enName ?? ''),
      기준인원: Number(src.기준인원 ?? 2),
      최대인원: Number(src.최대인원 ?? 2),
      추가비용부과: Boolean(src.추가비용부과 ?? false),
      성인추가금: Number(src.성인추가금 ?? 0),
      아동추가금: Number(src.아동추가금 ?? 0),
      무료아동수: Number(src.무료아동수 ?? 0),
      가격차등적용: Boolean(src.가격차등적용 ?? false),
      성인기준가: Number(src.성인기준가 ?? 0),
      아동기준가: Number(src.아동기준가 ?? 0),
      아동무료조건: Boolean(src.아동무료조건 ?? false),
      아동무료연령: Number(src.아동무료연령 ?? 5),
      조식포함: Boolean(src.조식포함 ?? false),
      태그: src.태그 ?? '',
      어메니티: normalizeAmenities(src.어메니티 ?? src.어메니티목록 ?? src.amenities ?? src.amenity ?? ''),
    });
  }, [isOpen, initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const requestClose = () => {
    setAnimatingOut(true);
    setMountedVisible(false);
    window.setTimeout(() => {
      onClose && onClose();
      setAnimatingOut(false);
    }, 220);
  };

  const handleSave = () => {
    // 유효성 검사
    if (!formData.객실명?.trim()) {
      alert('객실명을 입력해주세요.');
      return;
    }
    
    onSave && onSave(formData);
    requestClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 transition-opacity duration-200 ${
        animatingOut ? 'ease-in opacity-0' : 'ease-out'
      } ${mountedVisible && !animatingOut ? 'opacity-100' : 'opacity-0'}`}
      onMouseDown={(e) => {
        backdropMouseDownRef.current = (e.target === e.currentTarget);
      }}
      onMouseUp={(e) => {
        if (backdropMouseDownRef.current && e.target === e.currentTarget) {
          requestClose();
        }
        backdropMouseDownRef.current = false;
      }}
    >
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white shadow-2xl rounded-2xl z-50 max-h-[92vh] overflow-y-auto transform transition-all duration-200 ${
          animatingOut ? 'ease-in scale-95 opacity-0' : 'ease-out'
        } ${mountedVisible && !animatingOut ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {initialData ? '객실 수정' : '객실 추가'}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-3 space-y-4">
          {/* 기본 정보 */}
          <div>
            <h3 className="text-base font-medium text-gray-800 mb-2 px-2 py-1.5 bg-gray-100 rounded-md">
              기본 정보
            </h3>
            <div className="space-y-3">
              <FieldRow label="객실명" required>
                <InputField
                  name="객실명"
                  value={formData.객실명}
                  onChange={(e) => handleChange('객실명', e.target.value)}
                  placeholder="예: 디럭스 더블룸"
                />
              </FieldRow>
              <FieldRow label="영문명">
                <InputField
                  name="영문명"
                  value={formData.영문명}
                  onChange={(e) => handleChange('영문명', e.target.value)}
                  placeholder="예: Deluxe Double Room"
                />
              </FieldRow>
              <FieldRow label="어메니티">
                <KeywordsInput
                  value={formData.어메니티 || ''}
                  onChange={(val) => handleChange('어메니티', val)}
                  placeholder="어메니티 입력 후 Enter (예: WiFi, TV, 에어컨)"
                />
              </FieldRow>
            </div>
          </div>

          {/* 인원 설정 */}
          <div>
            <h3 className="text-base font-medium text-gray-800 mb-3 px-2 py-2 bg-gray-100 rounded-md">
              인원 설정
            </h3>
            <div className="space-y-3">
              <FieldRow label="기준인원">
                <div className="px-3 py-2">
                  <select
                    value={formData.기준인원}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      handleChange('기준인원', val);
                      // 기준인원 변경 시 최대인원도 자동 조정 (기준인원보다 작으면)
                      if (formData.최대인원 < val) {
                        handleChange('최대인원', val);
                      }
                    }}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                      <option key={n} value={n}>{n}명</option>
                    ))}
                  </select>
                  
                </div>
              </FieldRow>

              <FieldRow label="최대 투숙인원">
                <div className="px-3 py-2">
                  <select
                    value={formData.최대인원}
                    onChange={(e) => handleChange('최대인원', Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {[...Array(9)].map((_, i) => {
                      const n = i + 1;
                      return n >= formData.기준인원 ? (
                        <option key={n} value={n}>{n}명</option>
                      ) : null;
                    })}
                  </select>
                  
                </div>
              </FieldRow>

              <FieldRow label="추가비용 부과">
                <div className="px-3 py-2 space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.추가비용부과}
                      onChange={(e) => handleChange('추가비용부과', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">기준인원 초과 시 추가비용 부과</span>
                  </label>

                  {formData.추가비용부과 && (
                    <div className="ml-6 space-y-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">성인 추가금 (1인당)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumber(formData.성인추가금)}
                            onChange={(e) => handleChange('성인추가금', parseNumberInput(e.target.value))}
                            className="flex-1 px-3 py-2 border rounded"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-600">{currencySymbol}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">아동 추가금 (1인당)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumber(formData.아동추가금)}
                            onChange={(e) => handleChange('아동추가금', parseNumberInput(e.target.value))}
                            className="flex-1 px-3 py-2 border rounded"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-600">{currencySymbol}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">무료 아동 수 (기준인원 초과 시)</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="5"
                            value={formData.무료아동수}
                            onChange={(e) => handleChange('무료아동수', Number(e.target.value))}
                            className="flex-1 px-3 py-2 border rounded"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-600">명</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">기준인원 초과 시 무료로 투숙 가능한 아동 수</p>
                      </div>
                    </div>
                  )}
                </div>
              </FieldRow>
            </div>
          </div>

          {/* 객실 콘텐츠 연결 */}
          <div>
            <h3 className="text-base font-medium text-gray-800 mb-2 px-2 py-1.5 bg-gray-100 rounded-md">
              객실 콘텐츠 연결
            </h3>
            <div className="px-3 -mt-2 mb-2 text-xs text-gray-500">
              기존 콘텐츠를 선택해 연결하거나, 아래에서 새 콘텐츠를 추가하면 저장 후 자동으로 연결됩니다.
            </div>
            {!resourceNo && (
              <div className="px-3 mb-2 text-xs text-blue-600">
                객실을 먼저 저장하면 연결 버튼이 활성화됩니다.
              </div>
            )}
            <div className="space-y-3">
              {mappedContent ? (
                <div className="px-3 py-2">
                  <div className="mb-2 text-sm text-gray-700 font-medium">현재 연결된 콘텐츠</div>
                  <button
                    type="button"
                    className="w-full text-left flex items-start gap-3 p-3 border rounded bg-white hover:bg-gray-50"
                    onClick={() => {
                      const cno = mappedContent?.컨텐츠번호 ? Number(mappedContent.컨텐츠번호) : null;
                      if (cno && onEditMappedContent) onEditMappedContent(cno, mappedContent?.컨텐츠명 || '');
                    }}
                    title="콘텐츠 수정"
                  >
                    {mappedContent.photo ? (
                      <img src={mappedContent.photo} alt={mappedContent.컨텐츠명 || ''} className="w-20 h-20 object-cover rounded border" />
                    ) : null}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{mappedContent.컨텐츠명 || '-'}</div>
                      {mappedContent.내용 ? (
                        <div className="mt-1 text-xs text-gray-700 line-clamp-3 break-words">{mappedContent.내용}</div>
                      ) : (
                        <div className="mt-1 text-xs text-gray-400">내용 없음</div>
                      )}
                    </div>
                  </button>
                </div>
              ) : (
                <FieldRow label="콘텐츠 선택">
                  <div className="px-3 py-2 flex items-center gap-2">
                    <select
                      className="flex-1 px-3 py-2 border rounded"
                      value={selectedContentNo}
                      onChange={(e) => setSelectedContentNo(e.target.value)}
                    >
                      <option value="">선택하세요</option>
                      {Array.isArray(contentOptions) && contentOptions.map((o) => (
                        <option key={String(o.value)} value={String(o.value)}>{o.label || o.value}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      disabled={!selectedContentNo || !onMapContent || !resourceNo}
                      onClick={() => {
                        if (!selectedContentNo || !resourceNo) return;
                        onMapContent && onMapContent({ resourceNo, contentNo: Number(selectedContentNo) });
                      }}
                    >
                      연결
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 border rounded hover:bg-gray-50"
                      disabled={!onCreateAndMapContent || !resourceNo}
                      onClick={() => {
                        if (!resourceNo) return;
                        onCreateAndMapContent && onCreateAndMapContent({ resourceNo, roomName: formData.객실명 });
                      }}
                      title={!resourceNo ? '객실을 먼저 저장하면 추가가 가능합니다.' : '새 콘텐츠를 추가하고 자동으로 연결합니다.'}
                    >
                      추가
                    </button>
                  </div>
                </FieldRow>
              )}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center">
          {/* 삭제 버튼 (편집 모드일 때만 표시) */}
          {initialData && onDelete ? (
            <button
              onClick={() => {
                if (window.confirm(`"${formData.객실명}" 객실 타입을 삭제하시겠습니까?\n연결된 모든 상품도 함께 삭제됩니다.`)) {
                  onDelete();
                  requestClose();
                }
              }}
              className="px-5 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors font-medium"
            >
              삭제
            </button>
          ) : (
            <div></div>
          )}
          
          {/* 취소/저장 버튼 */}
          <div className="flex gap-3">
            <button
              onClick={requestClose}
              className="px-5 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomSettingsModal;
